import {
  SalaryForceLoginType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { useCallback, useEffect } from 'react'
import posthog from 'posthog-js'

const PUBLIC_SALARY_INTERACTIONS_LIMIT = process.env
  .NEXT_PUBLIC_SALARY_REG_WALL_LIMIT
  ? Number(process.env.NEXT_PUBLIC_SALARY_REG_WALL_LIMIT)
  : 4

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL

type UseForceLoginResponse = {
  countPublicInteraction: () => void
  publicInteractionLimitReached: boolean
}

type UseForceLoginProps = {
  hasPublicLimitCookie: boolean
  isAuthenticated: boolean
}

export function useForceLogin({
  hasPublicLimitCookie,
  isAuthenticated,
}: UseForceLoginProps): UseForceLoginResponse {
  const {
    incrementPublicInteractionCounter,
    publicInteractionCounter,
    setSalaryForceLoginType,
    hasShownForceLoginModal,
  } = useSalaryTableStore((state) => state)

  /**
   * If previously the user has reached the limit (cookie) or the user
   * has reached the limit in the current session.
   */
  const publicInteractionLimitReached =
    hasPublicLimitCookie ||
    publicInteractionCounter > PUBLIC_SALARY_INTERACTIONS_LIMIT

  /**
   * Sets a cookie in server side that will be used to determine if the user
   * has reached the limit after refresh or in future visits.
   */
  const showLimitModal = useCallback(() => {
    setSalaryForceLoginType(SalaryForceLoginType.Interaction)
    try {
      void fetch(`${BASE_URL}/api/salary-limit`, {
        method: 'GET',
        cache: 'no-store',
      })
    } catch (e) {
      posthog.captureException(e)
    }
  }, [setSalaryForceLoginType])

  useEffect(() => {
    if (publicInteractionLimitReached && !hasShownForceLoginModal) {
      showLimitModal()
    }
  }, [publicInteractionLimitReached, hasShownForceLoginModal, showLimitModal])

  const countPublicInteraction = () => {
    if (isAuthenticated || publicInteractionLimitReached) return
    incrementPublicInteractionCounter()
  }

  return {
    countPublicInteraction,
    publicInteractionLimitReached,
  }
}
